import { useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { Home, Power, User, ShieldCheck, Replace } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import AuthenticationService from '@/lib/auth';
import { Button } from '@/components/ui/button';
import auth from '@/lib/auth';

export default function Dashboard() {
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to sign-in page if not authenticated
    if (!AuthenticationService.isAuthenticated) {
      navigate('/authentication/sign-in');
    }
  }, [navigate]);

  const logout = async () => {
    await auth.logout();
    navigate('/authentication/sign-in');
  };

  if (!AuthenticationService.isAuthenticated) {
    return null;
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/40 sticky">
      <aside className="fixed inset-y-0 left-0 z-10 hidden w-14 flex-col border-r bg-background sm:flex">
        <nav className="flex flex-col items-center gap-4 px-2 sm:py-5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/dashboard/organizations"
                  className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-lg font-semibold text-primary-foreground md:h-8 md:w-8 md:text-base">
                  <Home className="h-5 w-5" />
                  <span className="sr-only">Organization</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom">Organizations</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* Users page */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/dashboard/users"
                  className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-lg font-semibold text-primary-foreground md:h-8 md:w-8 md:text-base">
                  <User className="h-5 w-5" />
                  <span className="sr-only">Users</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom">Users</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* Manage C8 user subscriptions */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/dashboard/staff"
                  className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-lg font-semibold text-primary-foreground md:h-8 md:w-8 md:text-base">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="sr-only">Manage C8 user subscriptions</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom">Manage C8 user subscriptions</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* Search Synonyms */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/dashboard/search-synonyms"
                  className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-lg font-semibold text-primary-foreground md:h-8 md:w-8 md:text-base">
                  <Replace className="h-5 w-5" />
                  <span className="sr-only">Search Synonyms</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom">Search Synonyms</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </nav>
        <nav className="mt-auto flex flex-col items-center gap-4 px-2 sm:py-5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="flex p-0 h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground md:h-8 md:w-8">
                  <Power className="h-5 w-5" />
                  <span className="sr-only">Logout</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Logout</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </nav>
      </aside>
      <div className="flex flex-col sm:gap-4 sm:py-4 sm:pl-14 sticky">
        <main className="grid flex-1 items-center gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
